import { __awaiter } from "tslib";
import * as React from 'react';
import cn from 'classnames';
import { useCallback, useState } from 'react';
import { Field, Form } from 'react-final-form';
import { useMediaQuery } from 'react-responsive';
import { useTranslation } from 'react-i18next';
import { InfoMessageTypeEnum } from '@websky/graphql';
import { BaseButton, Input, ModalX, Tooltip } from '../../../BaseComponents';
import { CdnIcon } from '@shared/ui';
import { useTheme } from '../../../theme';
import { MOBILE_MIN_WIDTH, useIsTablet } from '../../../utils';
import ClarifyDocumentModal from '../../../RetrieveBooking/components/ClarifyDocumentModal/ClarifyDocumentModal';
import { useClarifyDocumentModal } from '../../../RetrieveBooking/components/ClarifyDocumentModal/hooks';
import { useRetrieveBookingValidation } from '../../../RetrieveBooking/hooks';
import { isDifferentCurrenciesError } from '../../../Modules/Checkin/components/Checkin/Order/Passengers/utils';
import Notification from '../../../Notification';
var ButtonState;
(function (ButtonState) {
    ButtonState[ButtonState["Common"] = 0] = "Common";
    ButtonState[ButtonState["Loading"] = 1] = "Loading";
    ButtonState[ButtonState["Success"] = 2] = "Success";
})(ButtonState || (ButtonState = {}));
const AddPassengerForm = ({ onSubmit, onSuccess, onCancel, initialValues, hideIcon, buttonText, validationType, inputVariant }) => {
    const { t } = useTranslation('AddPassenger');
    const css = useTheme('AddPassenger').AddPassenger;
    const [isError, setIsError] = useState(false);
    const [isOpenModalInfo, setIsOpenModalInfo] = useState(false);
    const [buttonState, setButtonState] = useState(ButtonState.Common);
    const isMobile = useMediaQuery({ maxWidth: +MOBILE_MIN_WIDTH + 1 });
    const isTablet = useIsTablet();
    const { isOpen, open: openModal, searchParams, setSearchParams, close: closeModal, needToShowClarifyModal } = useClarifyDocumentModal();
    const { lastNameValidation, ticketNumberValidation, ticketNumberFormat } = useRetrieveBookingValidation(validationType);
    const submitForm = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () { return yield onSubmit(values['lastName'], values['id'], values['extraSecret']); }), [onSubmit]);
    const onSubmitHandler = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        setButtonState(ButtonState.Loading);
        try {
            const result = yield submitForm(values);
            if (result) {
                setButtonState(ButtonState.Success);
                setTimeout(() => {
                    onSuccess();
                    setButtonState(ButtonState.Common);
                }, 2000);
            }
            else {
                setButtonState(ButtonState.Common);
                setIsError(true);
            }
        }
        catch (e) {
            setButtonState(ButtonState.Common);
            if (needToShowClarifyModal(e)) {
                setSearchParams(values);
                openModal();
            }
            else if (isDifferentCurrenciesError(e)) {
                setIsError(false);
            }
            else {
                setIsError(true);
            }
        }
    }), [submitForm]);
    const handleClarifySubmit = useCallback((values) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const result = yield submitForm(values);
            if (result) {
                setButtonState(ButtonState.Success);
                setTimeout(() => {
                    onSuccess();
                    setButtonState(ButtonState.Common);
                }, 2000);
            }
            return result;
        }
        catch (e) {
            console.warn(e);
            return false;
        }
    }), [submitForm]);
    return (React.createElement(React.Fragment, null,
        React.createElement(ClarifyDocumentModal, { searchParams: searchParams, onSubmit: handleClarifySubmit, open: isOpen, onClose: closeModal }),
        React.createElement(Form, { onSubmit: onSubmitHandler, initialValues: initialValues, render: ({ handleSubmit, errors, touched }) => {
                const isIdFieldError = !!(errors === null || errors === void 0 ? void 0 : errors.id) && (touched === null || touched === void 0 ? void 0 : touched.id);
                return (React.createElement("form", { className: css.form, onSubmit: handleSubmit },
                    React.createElement("div", { className: css.fields },
                        isMobile && isError && (React.createElement(Notification, { type: "context", className: css.notification, contextClassName: css.notification_context, textClassName: css.notification__text, status: InfoMessageTypeEnum.Warning, infoMessage: {
                                type: InfoMessageTypeEnum.Warning,
                                text: t('Passenger not found'),
                                code: 'add-passenger-error'
                            } })),
                        React.createElement(Field, { name: "lastName", validate: (value, allValues, meta) => {
                                if (!value) {
                                    return t('Required field');
                                }
                                return lastNameValidation(value);
                            }, render: ({ input, meta }) => (React.createElement(Input, { className: css.form__input, name: "lastName", label: t('Passenger last name'), variant: inputVariant, InputProps: input, error: !!meta.error && meta.touched, helperText: !!meta.error && meta.touched ? meta.error : '', disabled: buttonState === ButtonState.Loading ||
                                    buttonState === ButtonState.Success })) }),
                        React.createElement(Field, { name: "id", parse: ticketNumberFormat, validate: (value) => {
                                if (!value) {
                                    return t('Required field');
                                }
                                return ticketNumberValidation(value);
                            }, render: ({ input, meta }) => (React.createElement(Input, { className: css.form__input, name: "id", label: t(validationType === 'ticketNumber'
                                    ? 'Ticket number'
                                    : 'Ticket number or booking reference'), variant: inputVariant, InputProps: input, error: (!!meta.error && meta.touched) || (!isMobile && isError), helperText: !!meta.error && meta.touched
                                    ? meta.error
                                    : !isMobile && isError
                                        ? t('Passenger not found')
                                        : '', disabled: buttonState === ButtonState.Loading ||
                                    buttonState === ButtonState.Success })) }),
                        !isTablet ? (React.createElement(Tooltip, { title: t('TicketInfoInReceipt') },
                            React.createElement("div", { className: cn(css.infoIcon, { [css.infoIcon__error]: isIdFieldError }) },
                                React.createElement(CdnIcon, { icon: 'Info/Question' })))) : (React.createElement(React.Fragment, null,
                            React.createElement("div", { className: cn(css.infoIcon, { [css.infoIcon__error]: isIdFieldError }), onClick: () => setIsOpenModalInfo(true) },
                                React.createElement(CdnIcon, { icon: 'Info/Question' })),
                            React.createElement(ModalX, { isOpen: isOpenModalInfo, onClose: () => setIsOpenModalInfo(false), title: React.createElement("span", { className: css.infoModalTitle }, t('WhereToFindTicketNumber')) }, t('TicketInfoInReceipt'))))),
                    React.createElement("div", { className: css.buttons },
                        onCancel && (React.createElement(BaseButton, { type: "button", className: css.cancelButton, variant: isMobile ? 'smallSecondary' : 'optionAction', onClick: onCancel }, t('Cancel'))),
                        React.createElement(BaseButton, { type: "submit", className: css.submitButton, variant: isMobile ? 'smallPrimary' : 'optionAction', isLoading: buttonState === ButtonState.Loading }, buttonText || t('Find')))));
            } })));
};
export default AddPassengerForm;
